<template>
  <el-dialog
    v-model="dialogVisible"
    title="Edit Program Price"
    class="new-library-dialog new-library-info-dialog with-prefix"
    modal-class="edit-program-price-dialog"
    width="640px"
    append-to-body
    :close-on-click-modal="false"
    align-center
    @close="handleClose"
  >
    <!-- Header prefix icon -->
    <svg class="dialog-prefix" xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
      <path d="M6.33333 13.4583H7.91667V12.6667H8.70833C9.12083 12.6667 9.45833 12.3292 9.45833 11.9167V9.5C9.45833 9.0875 9.12083 8.75 8.70833 8.75H6.33333V7.91667H9.45833V6.33333H7.91667V5.54167H6.33333V6.33333H5.54167C5.12917 6.33333 4.79167 6.67083 4.79167 7.08333V9.5C4.79167 9.9125 5.12917 10.25 5.54167 10.25H7.91667V11.0833H4.79167V12.6667H6.33333V13.4583ZM15.8333 3.16667H3.16667C2.29167 3.16667 1.58333 3.875 1.58333 4.75L1.575 14.25C1.575 15.125 2.29167 15.8333 3.16667 15.8333H15.8333C16.7083 15.8333 17.4167 15.125 17.4167 14.25V4.75C17.4167 3.875 16.7083 3.16667 15.8333 3.16667ZM15.8333 14.25H3.16667V4.75H15.8333V14.25ZM11.0833 7.91667L12.6667 6.33333L14.25 7.91667H11.0833ZM14.25 11.2917L12.6667 12.875L11.0833 11.2917" fill="white"/>
    </svg>
    <div class="program-price-content">
      <div class="form-field">
        <Input
          v-model="localPrice"
          InputTitle="Program Price"
          placeholder="Enter price"
          :clearable="true"
        />
      </div>
    </div>

    <!-- Footer Buttons -->
    <template #footer>
      <div class="dialog-footer footer-dash">
        <Button color-type="red-stroke" @click="handleClose">Cancel</Button>
        <Button color-type="dark-blue" :loading="saving" @click="handleSave">Save</Button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import Input from '@/NewLibrary/Input.vue';
import Button from '@/NewLibrary/Button.vue';
import { notify } from '@/utils/notify';
import { updateWorkflowApi } from '@/api/workflowManage';

export default {
  name: 'EditProgramPriceDialog',
  components: {
    Input,
    Button,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    programId: {
      type: [String, Number],
      default: null,
    },
    currentPrice: {
      type: [String, Number],
      default: '',
    },
  },
  emits: ['update:visible', 'price-updated'],
  data() {
    return {
      dialogVisible: false,
      localPrice: '',
      saving: false,
    };
  },
  watch: {
    visible: {
      handler(newVal) {
        this.dialogVisible = newVal;
        if (newVal) {
          this.localPrice = this.currentPrice ? String(this.currentPrice) : '';
        }
      },
      immediate: true,
    },
    dialogVisible(newVal) {
      if (!newVal) {
        this.$emit('update:visible', false);
      }
    },
  },
  methods: {
    handleClose() {
      this.dialogVisible = false;
      this.localPrice = '';
    },
    async handleSave() {
      if (!this.localPrice || !this.programId) {
        notify({
          title: 'Error',
          message: 'Please enter a valid price',
          type: 'error',
          showClose: true
        });
        return;
      }

      this.saving = true;
      try {
        const updatedPrice = parseFloat(this.localPrice);
        
        // Validate that the parsed price is a valid finite number
        if (!Number.isFinite(updatedPrice) || updatedPrice < 0) {
          notify({
            title: 'Validation Error',
            message: 'Please enter a valid price amount',
            type: 'warning',
            showClose: true
          });
          this.saving = false;
          return;
        }
        
        // Call API with only the price field
        await updateWorkflowApi(this.programId, {
          price: updatedPrice
        });
        
        notify({
          title: 'Success',
          message: 'Program price updated successfully',
          type: 'success',
          showClose: true
        });

        this.$emit('price-updated', {
          programId: this.programId,
          price: updatedPrice
        });

        this.handleClose();
      } catch (error) {
        console.error('Error updating program price:', error);
        notify({
          title: 'Error',
          message: error.response?.data?.message || 'Failed to update program price',
          type: 'error',
          showClose: true
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.program-price-content {
  padding: 0;
}

.form-field {
  margin-bottom: 24px;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  padding-top: 0;
  margin-top: 0;
}

.footer-dash {
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  padding-top: 16px;
}
</style>

<style>
/* Global dialog styles — same pattern as AddProductDialog */
.edit-program-price-dialog .el-dialog {
  --el-dialog-padding-primary: 0px !important;
  overflow: visible !important;
  background-color: #F9FBFD !important;
  border-radius: 10px !important;
}

.edit-program-price-dialog .el-dialog__body {
  overflow-y: auto !important;
  max-height: 70vh;
}

.edit-program-price-dialog .el-dialog__wrap {
  overflow: visible !important;
}

.edit-program-price-dialog .el-dialog__header {
  padding: 0;
  margin: 0;
}

.edit-program-price-dialog .el-dialog__header .el-dialog__title {
  font-family: Inter !important;
  font-size: 20px !important;
  font-weight: 600 !important;
}

.edit-program-price-dialog .el-dialog__body {
  padding: 16px 32px 0;
  margin: 0;
  background-color: #F9FBFD !important;
}

.edit-program-price-dialog .el-dialog__footer {
  padding: 0 32px 24px;
  margin: 0;
  background-color: #F9FBFD !important;
  border-radius: 0 0 10px 10px !important;
}

.edit-program-price-dialog .dialog-footer {
  width: 100%;
}

/* Footer buttons - Inter font */
.edit-program-price-dialog .dialog-footer :deep(button) {
  font-family: 'Inter', sans-serif !important;
  font-weight: 400 !important;
}
</style>
